import { useState, FormEvent, useEffect } from 'react'
import { router, useForm } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { CheckIcon, ChevronsUpDownIcon, LoaderIcon } from 'lucide-react'
import { AdminLayout } from '../../../layouts/admin-layout'
import { AppHeader } from '../../../components/app-header'
import { Card } from '../../../components/ui/card'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { Button } from '../../../components/ui/button'
import { Checkbox } from '../../../components/ui/checkbox'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select'
import { cn } from '../../../lib/utils'

interface Organization {
  id: number
  name: string
}

interface AdminDevInstancesNewProps {
  organizations: Organization[]
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
  flash?: {
    success?: string
    error?: string
    notice?: string
  }
}

function New({ organizations: initialOrganizations }: AdminDevInstancesNewProps) {
  const { t } = useTranslation('admin')
  const [createProject, setCreateProject] = useState(false)
  const [orgPopoverOpen, setOrgPopoverOpen] = useState(false)
  const [orgSearch, setOrgSearch] = useState('')
  const [organizations, setOrganizations] = useState<Organization[]>(initialOrganizations)
  const [isSearching, setIsSearching] = useState(false)
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null)
  const { data, setData, post, processing, errors } = useForm({
    rails_app_url: '',
    pilot_api_url: '',
    pilot_branch: '',
    project_type: 'django_app',
    source_repo_name: 'django-starter-base',
    organization_id: '',
    project_name: '',
    django_stack: true,
    instance_type: 't4g.medium',
  })

  const handleProjectTypeChange = (value: string) => {
    setData({
      ...data,
      project_type: value,
      django_stack: value === 'django_app',
      source_repo_name:
        value === 'managed_agent'
          ? 'agent-cayu-bootstrap'
          : value === 'django_app'
            ? 'django-starter-base'
            : 'starter-base',
    })
  }

  // Debounced search for organizations
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (orgSearch.length === 0) {
        setOrganizations(initialOrganizations)
        return
      }

      setIsSearching(true)
      try {
        const response = await fetch(`/admin/organizations/search?q=${encodeURIComponent(orgSearch)}`)
        const results = await response.json()
        setOrganizations(results)
      } catch (error) {
        console.error('Search failed:', error)
      } finally {
        setIsSearching(false)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [orgSearch, initialOrganizations])

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    post('/admin/dev_instances')
  }

  return (
    <>
      <AppHeader
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: t('devInstances.index.title'), href: '/admin/dev_instances' },
          { label: t('devInstances.new.title') }
        ]}
      />

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6 max-w-2xl">
              {/* Page Header */}
              <div className="mb-6">
                <h1 className="text-3xl font-bold text-foreground">{t('devInstances.new.title')}</h1>
                <p className="mt-2 text-sm text-muted-foreground">
                  {t('devInstances.new.description')}
                </p>
              </div>

              {/* Form */}
              <Card className="shadow-none p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Project Type */}
                  <div className="space-y-2">
                    <Label htmlFor="project_type">Project Type</Label>
                    <Select value={data.project_type} onValueChange={handleProjectTypeChange}>
                      <SelectTrigger id="project_type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="django_app">Django app</SelectItem>
                        <SelectItem value="managed_agent">Managed Agent</SelectItem>
                        <SelectItem value="rails_app">Rails app</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.project_type && (
                      <p className="text-sm text-red-600 dark:text-red-400">{errors.project_type}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Select the primary build target for this development instance.
                    </p>
                  </div>

                  {/* Source Repo Name */}
                  <div className="space-y-2">
                    <Label htmlFor="source_repo_name">{t('devInstances.new.form.sourceRepo')}</Label>
                    <Input
                      id="source_repo_name"
                      type="text"
                      placeholder={t('devInstances.new.form.sourceRepoPlaceholder')}
                      value={data.source_repo_name}
                      onChange={(e) => setData('source_repo_name', e.target.value)}
                    />
                    {errors.source_repo_name && (
                      <p className="text-sm text-red-600 dark:text-red-400">{errors.source_repo_name}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {t('devInstances.new.form.sourceRepoHint')}
                    </p>
                  </div>

                  {/* Instance Type */}
                  <div className="space-y-2">
                    <Label htmlFor="instance_type">Instance Type</Label>
                    <Select value={data.instance_type} onValueChange={(v) => setData('instance_type', v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="t4g.small">t4g.small — 2 GB, no workers ($12/mo)</SelectItem>
                        <SelectItem value="t4g.medium">t4g.medium — 4 GB, 2 workers ($25/mo)</SelectItem>
                        <SelectItem value="t4g.large">t4g.large — 8 GB, 3 workers ($49/mo)</SelectItem>
                        <SelectItem value="t4g.xlarge">t4g.xlarge — 16 GB, 4 workers ($98/mo)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Larger instances support more parallel workers for building features simultaneously
                    </p>
                  </div>

                  {/* Create Project Checkbox */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="create_project"
                        checked={createProject}
                        onCheckedChange={(checked) => {
                          setCreateProject(checked === true)
                          if (!checked) {
                            setData('organization_id', '')
                            setData('project_name', '')
                          }
                        }}
                      />
                      <Label htmlFor="create_project" className="cursor-pointer">
                        {t('devInstances.new.form.createProject')}
                      </Label>
                    </div>

                    {/* Organization Dropdown (shown when create project is checked) */}
                    {createProject && (
                      <>
                      <div className="space-y-2 ml-6">
                        <Label htmlFor="organization_id">{t('devInstances.new.form.organization')}</Label>
                        <Popover open={orgPopoverOpen} onOpenChange={setOrgPopoverOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={orgPopoverOpen}
                              className="w-full justify-between font-normal"
                            >
                              {selectedOrg?.name || t('devInstances.new.form.selectOrganization')}
                              <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                            <Command shouldFilter={false}>
                              <CommandInput
                                placeholder={t('projects.show.transferDialog.search')}
                                value={orgSearch}
                                onValueChange={setOrgSearch}
                              />
                              <CommandList>
                                {isSearching ? (
                                  <div className="py-6 text-center text-sm">
                                    <LoaderIcon className="mx-auto h-4 w-4 animate-spin" />
                                  </div>
                                ) : organizations.length === 0 ? (
                                  <CommandEmpty>{t('projects.show.transferDialog.noOrgFound')}</CommandEmpty>
                                ) : (
                                  <CommandGroup>
                                    {organizations.map((org) => (
                                      <CommandItem
                                        key={org.id}
                                        value={org.name}
                                        onSelect={() => {
                                          setData('organization_id', org.id.toString())
                                          setSelectedOrg(org)
                                          setOrgPopoverOpen(false)
                                          setOrgSearch('')
                                        }}
                                      >
                                        {org.name}
                                        <CheckIcon
                                          className={cn(
                                            "ml-auto h-4 w-4",
                                            data.organization_id === org.id.toString() ? "opacity-100" : "opacity-0"
                                          )}
                                        />
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                )}
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                        {errors.organization_id && (
                          <p className="text-sm text-red-600 dark:text-red-400">{errors.organization_id}</p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          {t('devInstances.new.form.organizationHint')}
                        </p>
                      </div>

                      <div className="space-y-2 ml-6">
                        <Label htmlFor="project_name">{t('devInstances.new.form.instanceName')}</Label>
                        <Input
                          id="project_name"
                          type="text"
                          placeholder={t('devInstances.new.form.instanceNamePlaceholder')}
                          value={data.project_name}
                          onChange={(e) => setData('project_name', e.target.value)}
                        />
                        {errors.project_name && (
                          <p className="text-sm text-red-600 dark:text-red-400">{errors.project_name}</p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          {t('devInstances.new.form.instanceNameHint')}
                        </p>
                      </div>
                      </>
                    )}
                  </div>

                  {/* Rails App URL */}
                  <div className="space-y-2">
                    <Label htmlFor="rails_app_url">{t('devInstances.new.form.railsAppUrl')}</Label>
                    <Input
                      id="rails_app_url"
                      type="url"
                      placeholder={t('devInstances.new.form.railsAppUrlPlaceholder')}
                      value={data.rails_app_url}
                      onChange={(e) => setData('rails_app_url', e.target.value)}
                    />
                    {errors.rails_app_url && (
                      <p className="text-sm text-red-600 dark:text-red-400">{errors.rails_app_url}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {t('devInstances.new.form.railsAppUrlHint')}
                    </p>
                  </div>

                  {/* Pilot API URL */}
                  <div className="space-y-2">
                    <Label htmlFor="pilot_api_url">{t('devInstances.show.urls.cayuPilot')} URL</Label>
                    <Input
                      id="pilot_api_url"
                      type="url"
                      placeholder="https://pilot.example.cayu.ai"
                      value={data.pilot_api_url}
                      onChange={(e) => setData('pilot_api_url', e.target.value)}
                    />
                    {errors.pilot_api_url && (
                      <p className="text-sm text-red-600 dark:text-red-400">{errors.pilot_api_url}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {t('devInstances.show.urls.description')}
                    </p>
                  </div>

                  {/* Pilot Branch */}
                  <div className="space-y-2">
                    <Label htmlFor="pilot_branch">{t('devInstances.new.form.pilotBranch')}</Label>
                    <Input
                      id="pilot_branch"
                      type="text"
                      placeholder="e.g., feature/my-branch"
                      className="font-mono"
                      value={data.pilot_branch}
                      onChange={(e) => setData('pilot_branch', e.target.value)}
                    />
                    {errors.pilot_branch && (
                      <p className="text-sm text-red-600 dark:text-red-400">{errors.pilot_branch}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {t('devInstances.new.form.pilotBranchHint')}
                    </p>
                  </div>

                  {/* Form Actions */}
                  <div className="flex gap-3 pt-4">
                    <Button type="submit" disabled={processing}>
                      {processing ? t('devInstances.new.buttons.creating') : t('devInstances.new.buttons.create')}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => router.visit('/admin/dev_instances')}
                      disabled={processing}
                    >
                      {t('devInstances.new.buttons.cancel')}
                    </Button>
                  </div>
                </form>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

New.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default New
